import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  costLineTotal,
  formatCents,
  jobCosting,
  parseCents,
  type JobCostItem,
} from '@fsm/shared';
import { api } from '../lib/api';
import { Button, Card, Input, cx } from './ui';

/**
 * What the job cost us, next to what it charged.
 *
 * Deliberately separate from the line items above it. Those are the billing
 * record: they are copied onto the invoice, the customer reads them, and they
 * freeze once money moves. These are supplier prices and subcontractor hours —
 * they never leave the office, and they stay editable forever.
 *
 * None of the arithmetic is here. `jobCosting` in the shared package owns it,
 * so the day a report wants margin by technician it gets the same number this
 * card shows rather than a second opinion.
 */
export function JobCostSheet({
  jobId,
  items,
  internalDiscountCents,
  billedTotalCents,
  currency,
  onChanged,
  onError,
}: {
  jobId: string;
  items: JobCostItem[];
  internalDiscountCents: number;
  /** null when nothing has been priced yet. */
  billedTotalCents: number | null;
  currency?: string;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', quantity: '1', cost: '' });
  const [discount, setDiscount] = useState<string | null>(null);

  const money = (cents: number) => formatCents(cents, currency);

  const costing = jobCosting({
    items: items.map((item) => ({
      quantity: item.quantity,
      unitCostCents: item.unitCostCents,
    })),
    internalDiscountCents,
    billedTotalCents,
  });

  const add = useMutation({
    mutationFn: () =>
      api.jobs.addCostItem(jobId, {
        name: form.name.trim(),
        quantity: Number(form.quantity) || 1,
        unitCostCents: parseCents(form.cost) ?? 0,
      }),
    onSuccess: () => {
      setForm({ name: '', quantity: '1', cost: '' });
      setAdding(false);
      onChanged();
    },
    onError: (e) => onError((e as { message?: string }).message ?? 'Could not add that line'),
  });

  const remove = useMutation({
    mutationFn: (costItemId: string) => api.jobs.removeCostItem(jobId, costItemId),
    onSuccess: onChanged,
    onError: (e) => onError((e as { message?: string }).message ?? 'Could not remove that line'),
  });

  const saveDiscount = useMutation({
    mutationFn: () =>
      api.jobs.update(jobId, { internalDiscountCents: parseCents(discount ?? '0') ?? 0 }),
    onSuccess: () => {
      setDiscount(null);
      onChanged();
    },
    onError: (e) => onError((e as { message?: string }).message ?? 'Could not save the discount'),
  });

  const canAdd = form.name.trim() && (parseCents(form.cost) ?? 0) > 0;

  return (
    <Card
      title="Internal cost sheet"
      action={
        !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Add a cost
          </Button>
        )
      }
      bodyClassName="p-0"
    >
      <p className="px-4 pt-3 pb-1 text-[12px] text-ink-4">
        Office only. Nothing here reaches the invoice, the customer's status page or an email.
      </p>

      {items.length === 0 && !adding ? (
        <p className="px-4 py-4 text-center text-[12.5px] text-ink-4">
          Nothing recorded. Add what the parts and the subcontractor actually cost.
        </p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-y border-line bg-raised text-[12px] font-medium text-ink-3">
              <th className="px-4 py-2 text-left font-semibold">Item</th>
              <th className="px-4 py-2 text-right font-semibold">Qty</th>
              <th className="px-4 py-2 text-right font-semibold">Unit cost</th>
              <th className="px-4 py-2 text-right font-semibold">Cost</th>
              <th className="w-[40px]" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-line">
                <td className="px-4 py-2.5">
                  <div>{item.name}</div>
                  {item.note && <div className="text-[11.5px] text-ink-4">{item.note}</div>}
                </td>
                <td className="px-4 py-2.5 text-right tnum">{item.quantity}</td>
                <td className="px-4 py-2.5 text-right tnum">{money(item.unitCostCents)}</td>
                <td className="px-4 py-2.5 text-right tnum font-medium">
                  {money(costLineTotal(item))}
                </td>
                <td className="px-2 py-2.5 text-right">
                  <button
                    type="button"
                    onClick={() => remove.mutate(item.id)}
                    title="Remove this line"
                    className="text-[15px] leading-none text-ink-4 hover:text-danger"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <div className="flex flex-wrap items-end gap-2 border-b border-line bg-raised px-4 py-3">
          <label className="min-w-[180px] flex-1">
            <span className="mb-1 block text-[11.5px] text-ink-4">What was it</span>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Blower motor, supplier"
              autoFocus
            />
          </label>
          <label className="w-[72px]">
            <span className="mb-1 block text-[11.5px] text-ink-4">Qty</span>
            <Input
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              inputMode="decimal"
              className="tnum"
            />
          </label>
          <label className="w-[110px]">
            <span className="mb-1 block text-[11.5px] text-ink-4">Unit cost</span>
            <Input
              value={form.cost}
              onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
              inputMode="decimal"
              placeholder="0.00"
              className="tnum"
            />
          </label>
          <Button variant="primary" disabled={!canAdd} loading={add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      <div className="space-y-1.5 px-4 py-3 text-[13px]">
        <Row label="Cost" value={money(costing.grossCostCents)} />

        <div className="flex items-center justify-between gap-3">
          <span className="text-ink-3">Internal discount</span>
          {discount === null ? (
            <button
              type="button"
              onClick={() => setDiscount((internalDiscountCents / 100).toFixed(2))}
              className="tnum text-ink-2 hover:text-accent"
            >
              −{money(costing.internalDiscountCents)}
            </button>
          ) : (
            <span className="flex items-center gap-1.5">
              <Input
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                inputMode="decimal"
                className="h-7 w-[88px] tnum text-right"
                autoFocus
              />
              <Button
                size="sm"
                variant="primary"
                loading={saveDiscount.isPending}
                onClick={() => saveDiscount.mutate()}
              >
                Save
              </Button>
              <Button size="sm" onClick={() => setDiscount(null)}>
                Cancel
              </Button>
            </span>
          )}
        </div>

        <Row label="Net cost" value={money(costing.netCostCents)} strong />

        {/*
         * Margin is the only number on this card anybody looks at twice, so it
         * gets the line to itself and says plainly when it cannot be computed
         * rather than printing a confident zero.
         */}
        <div className="flex items-baseline justify-between gap-3 border-t border-line pt-2">
          <span className="text-ink-3">
            Margin
            {billedTotalCents !== null && (
              <span className="ml-1.5 text-[11.5px] text-ink-4">
                against {money(billedTotalCents)} billed
              </span>
            )}
          </span>
          {costing.marginCents === null ? (
            <span className="text-[12.5px] text-ink-4">nothing priced yet</span>
          ) : (
            <span
              className={cx(
                'tnum text-[15px] font-semibold',
                costing.marginCents < 0 ? 'text-danger' : 'text-good',
              )}
            >
              {money(costing.marginCents)}
              {costing.marginBps !== null && (
                <span className="ml-1.5 text-[12px] font-normal text-ink-4">
                  {(costing.marginBps / 100).toFixed(1)}%
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-ink-3">{label}</span>
      <span className={cx('tnum', strong && 'font-semibold')}>{value}</span>
    </div>
  );
}
